/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Stack, Box, Text, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import Field from '../../../components/field'
import useLoginFields from '../../../components/forms/useLoginFields'
import {
    errorTextLogin,
    forgotPasswordButtonLogin,
    forgotPasswordWrapper
} from '../../login/index.style'

const LoginFields = ({
    form,
    handleForgotPasswordClick,
    handleEditEmailClick,
    prefix = '',
    hideEmail = false,
    hidePassword = false
}) => {
    const fields = useLoginFields({form, prefix})

    const emailValue = form.watch(`${prefix}email`)

    useEffect(() => {
        if (emailValue && typeof window !== 'undefined') {
            localStorage.setItem('passwordResetEmail', emailValue)
        }
    }, [emailValue])

    return (
        <Stack spacing={5}>
            <Stack display={hideEmail ? 'none' : 'initial'}>
                <Field {...fields.email} readOnly={hideEmail} />
            </Stack>
            {!hidePassword && (
                <Stack className="password-container">
                    <Text className="email-entered">
                        {form.getValues('email')}
                        <Text as="span" onClick={handleEditEmailClick}>
                            <FormattedMessage defaultMessage="Edit" id="login_form.password.edit" />
                        </Text>
                    </Text>
                    <Field {...fields.password} />
                    <Box sx={forgotPasswordWrapper}>
                        {form?.formState?.errors?.global?.message?.includes(
                            'Password incorrect'
                        ) && (
                            <Text as="p" sx={errorTextLogin}>
                                {form?.formState?.errors?.global?.message}
                            </Text>
                        )}

                        {handleForgotPasswordClick && (
                            <Button
                                variant="link"
                                size="sm"
                                onClick={handleForgotPasswordClick}
                                sx={forgotPasswordButtonLogin}
                            >
                                <FormattedMessage
                                    defaultMessage="Forgot Password?"
                                    id="contact_info.link.forgot_password_capitalize"
                                />
                            </Button>
                        )}
                    </Box>
                </Stack>
            )}
        </Stack>
    )
}

LoginFields.propTypes = {
    handleForgotPasswordClick: PropTypes.func,
    handleEditEmailClick: PropTypes.func,

    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Optional configurations */
    hideEmail: PropTypes.bool,
    hidePassword: PropTypes.bool
}

export default LoginFields
